/**
 * Client-side rules for the `user_aggregates.refreshing` lock (issue #440).
 *
 * The lock is a *server* coordination flag: the dashboard writes it before calling
 * `apiAggregatesInitiate`, and `buildAggregates` releases it when the rebuild lands.
 * It is not UI state. Hydrating it into the dashboard slice meant a lock stranded by a
 * failed build permanently disabled every aggregate-backed widget
 * (@see components/widgets/Widget.js), on every subsequent page load.
 *
 * Kept dependency-free so it can be unit tested with `node --test`.
 * @see apps/backend/functions/utilities/aggregateLock.js for the server half.
 */

/**
 * Aggregate-doc keys that must never overwrite dashboard UI state.
 *
 * `refreshing`/`refreshingAt` are the server lock. `loading` and `delaying` are listed
 * defensively — they are client-owned names that would be clobbered if a future
 * aggregate payload ever carried them.
 */
export const SERVER_OWNED_STAT_KEYS = new Set(['refreshing', 'refreshingAt', 'loading', 'delaying']);

/** Mirror of AGGREGATE_LOCK_STALE_SECONDS in the backend helper — keep the two in sync. */
export const AGGREGATE_LOCK_STALE_SECONDS = 600;

/**
 * How long the client waits for the server to release the lock before giving up.
 *
 * Bounds the spinner: a build killed by timeout or OOM never clears the flag, and
 * without this the listener would wait — and the UI spin — forever.
 */
export const AGGREGATE_REFRESH_TIMEOUT_MS = 180_000;

/**
 * Copies aggregate stats onto dashboard state, skipping server-owned keys.
 *
 * Mutates `state` in place: callers are Immer reducers, whose drafts behave like plain
 * objects, so tests can pass plain objects.
 *
 * @param {object} state Dashboard slice state (or draft).
 * @param {object|null|undefined} payload `user_aggregates` document data.
 * @returns {object} The same `state`, for convenience.
 */
export const copyStatsIntoState = (state, payload) => {
    if (!payload) {
        return state;
    }
    for (const key in payload) {
        if (SERVER_OWNED_STAT_KEYS.has(key)) {
            continue;
        }
        // Unknown keys are ignored: initialState is the schema for what the dashboard renders.
        if (key in state) {
            state[key] = payload[key];
        }
    }
    return state;
};

/**
 * Whether a rebuild is already running, so a second one must not be initiated.
 *
 * A stale or untimestamped lock is reported dead — that is the bounded recovery that
 * lets a user whose build died get a working dashboard back without support action.
 *
 * @param {object|null|undefined} aggDoc `user_aggregates` document data.
 * @param {number} nowUnix Current unix seconds.
 * @returns {boolean}
 */
export const isAggregateRebuildRunning = (aggDoc, nowUnix) => {
    if (!aggDoc || !aggDoc.refreshing) {
        return false;
    }
    const startedAt = parseInt(aggDoc.refreshingAt, 10);
    if (!Number.isFinite(startedAt) || startedAt <= 0) {
        return false;
    }
    return nowUnix - startedAt < AGGREGATE_LOCK_STALE_SECONDS;
};
